import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  AGENT_AUTH_DAYS,
  type Asset,
  type AssetKind,
  createVaultSchema,
  defaultChain,
  defaultRules,
  formatPct,
  formatUsd,
  normalizeBps,
  oracleDeviationBps,
  type Rules,
  rulesSchema,
  shortAddress,
  unpricedAssets,
  type VaultDetail,
  type VaultSummary,
  validateTargets,
} from "@v4ult/shared";
import { bipsFactoryAbi } from "@v4ult/shared/abi";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Loader2,
  Lock,
  Pencil,
  Plus,
  Search,
  Trash2,
  Wallet,
  X,
} from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { type ReactNode, useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { erc20Abi, formatUnits, keccak256, parseUnits, toHex, zeroAddress } from "viem";
import { useAccount, usePublicClient, useReadContract, useSwitchChain, useWriteContract } from "wagmi";
import { AllocBar, AllocPie, APP_WRAP, GuardItem, mixColor, OptionSeg, Seg } from "../components/app-ui";
import { AssetIcon, KindTag } from "../components/asset";
import { useWalletStep } from "../components/wallet";
import { type AppConfig, api, createVault, useAppConfig } from "../lib/api";
import {
  type PendingVault,
  readPendingVault,
  recoverPendingVault,
  savePendingVault,
  submitPendingVault,
} from "../lib/pending-vault";
import { getToken } from "../lib/session";
import { actionGuard } from "../lib/transactions";
import { cn } from "../lib/ui";
import type { SupportedChainId } from "../lib/wagmi";
import { guardrails } from "../site/content";

export const Route = createFileRoute("/app/new")({ component: Builder });

interface Row {
  symbol: string;
  bps: number;
}

const PRESETS: { name: string; rows: Row[]; chain: number }[] = [
  {
    name: "Frontier tech",
    chain: defaultChain.id,
    rows: [
      { symbol: "SPCX", bps: 2500 },
      { symbol: "NVDA", bps: 2000 },
      { symbol: "TSLA", bps: 1500 },
      { symbol: "WETH", bps: 1000 },
      { symbol: "USDG", bps: 3000 },
    ],
  },
  {
    name: "Index barbell",
    chain: defaultChain.id,
    rows: [
      { symbol: "SPY", bps: 3500 },
      { symbol: "QQQ", bps: 2500 },
      { symbol: "SGOV", bps: 2000 },
      { symbol: "USDG", bps: 2000 },
    ],
  },
  {
    name: "Magnificent mix",
    chain: defaultChain.id,
    rows: [
      { symbol: "AAPL", bps: 1400 },
      { symbol: "MSFT", bps: 1400 },
      { symbol: "NVDA", bps: 1400 },
      { symbol: "GOOGL", bps: 1400 },
      { symbol: "AMZN", bps: 1200 },
      { symbol: "META", bps: 1200 },
      { symbol: "TSLA", bps: 1000 },
      { symbol: "USDG", bps: 1000 },
    ],
  },
  {
    name: "Crypto core",
    chain: defaultChain.id,
    rows: [
      { symbol: "cbBTC", bps: 3500 },
      { symbol: "WETH", bps: 3500 },
      { symbol: "USDG", bps: 3000 },
    ],
  },
];

const BANDS: { v: number; note: string }[] = [
  { v: 300, note: "Tight · trades more" },
  { v: 500, note: "Balanced" },
  { v: 750, note: "Relaxed" },
  { v: 1000, note: "Loose · trades least" },
];
const INTERVALS = [
  { v: 15, l: "15 min", note: "Most responsive" },
  { v: 60, l: "Hourly", note: "Balanced" },
  { v: 240, l: "4 hours", note: "Calmer" },
  { v: 1440, l: "Daily", note: "Fewest checks" },
] as const;
const SLIPPAGE = [
  { v: 30, note: "Strict" },
  { v: 50, note: "Standard" },
  { v: 100, note: "Loose" },
];
const COSTS = [
  { v: 30, note: "Strict" },
  { v: 60, note: "Standard" },
  { v: 100, note: "Loose" },
];

const STEPS = ["Mix", "Rules", "Deposit", "Review"] as const;
type Step = 0 | 1 | 2 | 3;
const DEPOSIT: Step = 2;
const REVIEW: Step = 3;
const AMOUNTS = [100, 500, 1000, 5000];

/** Every vault keeps its deposit stablecoin as a row: deposits land in it, at 0% it's all invested. */
const withCash = (rows: Row[], cash: string): Row[] =>
  rows.some((r) => r.symbol === cash) ? rows : [...rows, { symbol: cash, bps: 0 }];

/** The deposit as token units, or null while the field is empty or not a number. */
function depositUnits(amount: string, decimals: number): bigint | null {
  if (!/^\d+(\.\d*)?$|^\.\d+$/.test(amount.trim())) return null;
  try {
    return parseUnits(amount.trim(), decimals);
  } catch {
    return null;
  }
}

const DRAFT_KEY = "bips.draft";
interface Draft {
  chainId: number;
  name: string;
  rows: Row[];
  rules: Rules;
  amount: string;
}

/** The builder as the visitor left it. Signing in remounts the app, so this keeps their work for the page's life. */
let inProgress: (Draft & { step: Step; allowUnpriced: boolean }) | null = null;

function readDraft(): Draft | null {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const d = JSON.parse(raw) as Partial<Draft>;
    if (d.chainId !== defaultChain.id || !Array.isArray(d.rows) || !rulesSchema.safeParse(d.rules).success)
      return null;
    return {
      chainId: d.chainId,
      name: typeof d.name === "string" ? d.name : "",
      rows: d.rows
        .filter((r): r is Row => typeof r?.symbol === "string" && Number.isInteger(r?.bps) && r.bps >= 0)
        .slice(0, 12),
      rules: { ...defaultRules, ...d.rules },
      amount: typeof d.amount === "string" ? d.amount : "",
    };
  } catch {
    return null;
  }
}

/** Empty, or a preset's name we filled in: safe to replace with the next suggestion. */
const autoName = (n: string) => !n.trim() || PRESETS.some((p) => p.name === n);

const intervalLabel = (m: number) => INTERVALS.find((i) => i.v === m)?.l ?? `${m} min`;

function Builder() {
  const { data: config, isPending: configPending, error: configError } = useAppConfig();
  const { address } = useAccount();
  const [pendingVault, setPendingVault] = useState(() => readPendingVault(address));
  const [carried] = useState(() => inProgress);
  const [draft] = useState(() =>
    pendingVault
      ? {
          chainId: pendingVault.input.chainId,
          name: pendingVault.input.name,
          rows: pendingVault.input.targets.map((t) => ({ symbol: t.symbol, bps: t.weightBps })),
          rules: pendingVault.input.rules,
          amount: "",
        }
      : (carried ?? readDraft()),
  );
  const [restored, setRestored] = useState(!!draft && !carried);
  const [step, setStep] = useState<Step>(pendingVault ? REVIEW : (carried?.step ?? 0));
  // One network: every vault lives on the default chain, funded in its stablecoin.
  const chainId = defaultChain.id;
  // The asset list ships with the app, so the builder works before (or without) the API's config.
  const chain = config?.chains.find((c) => c.id === chainId) ?? defaultChain;
  const cash = chain.quoteAsset;
  const [name, setName] = useState(draft?.name ?? PRESETS[0]!.name);
  const [rows, setRowsRaw] = useState<Row[]>(() => withCash(draft?.rows ?? PRESETS[0]!.rows, cash));
  const setRows: React.Dispatch<React.SetStateAction<Row[]>> = useCallback(
    (next) => setRowsRaw((prev) => withCash(typeof next === "function" ? next(prev) : next, cash)),
    [cash],
  );
  const [amount, setAmount] = useState(draft?.amount || "1000");
  const [rules, setRules] = useState<Rules>(draft?.rules ?? defaultRules);
  const [allowUnpriced, setAllowUnpriced] = useState(carried?.allowUnpriced ?? false);
  const [busy, setBusy] = useState(false);
  const created = useRef(false);
  useEffect(() => {
    if (!created.current) inProgress = { chainId, name, rows, rules, amount, step, allowUnpriced };
  }, [name, rows, rules, amount, step, allowUnpriced]);

  useEffect(() => {
    const saved = readPendingVault(address);
    setPendingVault(saved);
    if (saved) {
      setName(saved.input.name);
      setRows(saved.input.targets.map((t) => ({ symbol: t.symbol, bps: t.weightBps })));
      setRules(saved.input.rules);
      setStep(REVIEW);
    }
  }, [address, setRows]);

  const total = rows.reduce((s, r) => s + r.bps, 0);
  const unknown = rows.find((r) => !chain.assets.some((a) => a.symbol === r.symbol));
  const error =
    validateTargets(
      rows.map((r) => ({ symbol: r.symbol, weightBps: r.bps })),
      { cash },
    ) ?? (unknown ? `${unknown.symbol} is not available on ${chain.name}` : null);
  const assetOf = (s: string) => chain.assets.find((a) => a.symbol === s);

  // The first deposit: an amount of the cash stablecoin, checked against the wallet's balance.
  const cashAsset = assetOf(cash);
  const balance = useReadContract({
    address: cashAsset?.address,
    abi: erc20Abi,
    functionName: "balanceOf",
    args: address ? [address] : undefined,
    chainId: chainId as SupportedChainId,
    query: { enabled: !!address && !!cashAsset, refetchInterval: 30_000 },
  });
  const units = cashAsset ? depositUnits(amount, cashAsset.decimals) : null;
  const amountUsd = Number(amount) || 0;
  const amountError = !cashAsset
    ? null
    : !units || units === 0n
      ? `Enter how much ${cash} to deposit`
      : amountUsd < rules.minTradeUsd
        ? `Deposit at least ${formatUsd(rules.minTradeUsd)}, your minimum swap`
        : balance.data !== undefined && units > balance.data
          ? `Your wallet holds ${formatUnits(balance.data, cashAsset.decimals)} ${cash}`
          : null;

  const saveDraft = () => {
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ chainId, name, rows, rules, amount }));
      toast.success("Draft saved on this device");
    } catch {
      toast.error("This browser couldn't save the draft");
    }
  };
  const startOver = () => {
    localStorage.removeItem(DRAFT_KEY);
    setRestored(false);
    setName(PRESETS[0]!.name);
    setRows(PRESETS[0]!.rows);
    setRules(defaultRules);
  };

  // A step can be opened once everything before it is valid: the mix, then the deposit.
  const canReach = (s: Step) =>
    !busy && !pendingVault && (s === 0 || (!error && (s <= DEPOSIT || !amountError)));
  // Moving between steps: back to the top, focus on the new heading.
  const heading = useRef<HTMLHeadingElement>(null);
  const go = (s: Step) => {
    if (!canReach(s)) return;
    setStep(s);
    requestAnimationFrame(() => {
      const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      window.scrollTo({ top: 0, behavior: reduce ? "auto" : "smooth" });
      heading.current?.focus({ preventScroll: true });
    });
  };

  const notes = [
    rows.length
      ? `${rows.length} asset${rows.length === 1 ? "" : "s"} · ${formatPct(total, total % 100 ? 1 : 0)}`
      : "No assets yet",
    `±${rules.driftThresholdBps / 100} pts · ${intervalLabel(rules.checkIntervalMin).toLowerCase()} checks`,
    amountUsd > 0 ? `${formatUsd(amountUsd)} in ${cash}` : `First deposit in ${cash}`,
    "Review and create",
  ];

  const head = [
    {
      title: "Build your mix",
      lead: "Pick up to 12 assets per vault and their target weights, or start from a preset and adjust it.",
    },
    {
      title: "Set the rules",
      lead: "When the agent acts and how carefully it trades. The vault contract enforces the limits, not the agent.",
    },
    {
      title: "Fund your vault",
      lead: `Your first deposit is in ${cash}. It lands in your vault in the same transaction that creates it, and the agent buys your mix with it at the best quote.`,
    },
    {
      title: "Review your vault",
      lead: "This is the vault you'll create. You can change the mix and rules later; the agent can't.",
    },
  ][step]!;

  return (
    <div
      className={`${APP_WRAP} grid grid-cols-[minmax(0,1fr)] gap-8 pt-7 pb-12 lg:grid-cols-[200px_minmax(0,1fr)_360px] lg:gap-10 lg:pt-14 lg:pb-28`}
    >
      <Rail step={step} notes={notes} canReach={canReach} onGo={go} />

      <div className="grid min-w-0 grid-cols-[minmax(0,1fr)] content-start gap-6">
        <div>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="ap-mono flex flex-wrap items-center gap-2">
              Step {step + 1} of {STEPS.length} · {chain.name}
              {restored && step === 0 && (
                <span className="inline-flex items-center gap-2 normal-case tracking-normal">
                  <span aria-hidden="true">·</span>
                  <span className="font-sans text-[13px] text-(--ap-ink)">Draft restored</span>
                  <button
                    type="button"
                    onClick={startOver}
                    className="font-sans text-[13px] font-[550] text-(--ap-pink) underline decoration-(--ap-pink)/30 underline-offset-4"
                  >
                    Start over
                  </button>
                </span>
              )}
            </p>
            <div className="flex items-center gap-1.5">
              <button type="button" onClick={saveDraft} className="ap-btn ap-btn-quiet">
                Save draft
              </button>
              <Link to="/app" aria-label="Close and go back to your vaults" className="ap-iconbtn">
                <X size={18} aria-hidden="true" />
              </Link>
            </div>
          </div>
          <h1 ref={heading} tabIndex={-1} className="ap-title mt-3">
            {head.title}
          </h1>
          <p className="mt-3 max-w-[580px] text-[16px] leading-[1.5] text-(--ap-grey)">{head.lead}</p>
        </div>

        {/* keyed by step: each step fades in instead of popping */}
        <div key={step} className="ix-enter grid min-w-0 grid-cols-[minmax(0,1fr)] gap-6">
          {step === 0 && (
            <MixStep
              config={config}
              chainId={chainId}
              rows={rows}
              setRows={setRows}
              assetOf={assetOf}
              onPreset={(p) => {
                setRows(p.rows);
                if (autoName(name)) setName(p.name);
              }}
            />
          )}
          {step === 1 && <RulesStep rules={rules} setRules={setRules} />}
          {step === DEPOSIT && (
            <DepositStep
              cash={cash}
              cashAsset={cashAsset}
              amount={amount}
              setAmount={setAmount}
              balance={balance.data}
              error={amountError}
              rows={rows}
              assetOf={assetOf}
            />
          )}
          {step === REVIEW && (
            <ReviewStep
              name={name}
              setName={setName}
              locked={busy || !!pendingVault}
              rows={rows}
              rules={rules}
              amountUsd={amountUsd}
              cash={cash}
              assetOf={assetOf}
            />
          )}
        </div>

        <StepNav step={step} onGo={go} busy={busy} />
      </div>

      {step < REVIEW && (
        <MobileNextBar
          rows={rows}
          total={total}
          rules={rules}
          step={step}
          error={step === DEPOSIT ? amountError : error}
          amountUsd={amountUsd}
          cash={cash}
          onNext={() => go((step + 1) as Step)}
        />
      )}

      <aside
        aria-label={step === REVIEW ? "Create your vault" : "Your vault so far"}
        className={cn("lg:sticky lg:top-[100px] lg:self-start", step < REVIEW && "max-lg:hidden")}
      >
        {step < REVIEW ? (
          <SoFar
            rows={rows}
            rules={rules}
            total={total}
            amountUsd={step >= DEPOSIT ? amountUsd : null}
            cash={cash}
            error={step === DEPOSIT ? amountError : error}
            step={step}
            assetOf={assetOf}
            onNext={() => go((step + 1) as Step)}
          />
        ) : (
          <DeployPanel
            config={config}
            configPending={configPending}
            configError={configError?.message ?? null}
            chainId={chainId}
            name={name}
            rows={rows}
            rules={rules}
            error={error ?? amountError}
            cashAsset={cashAsset}
            deposit={units}
            amountUsd={amountUsd}
            assetOf={assetOf}
            allowUnpriced={allowUnpriced}
            setAllowUnpriced={setAllowUnpriced}
            onBusy={setBusy}
            pendingVault={pendingVault}
            onPending={setPendingVault}
            onDone={() => {
              created.current = true;
              inProgress = null;
              try {
                localStorage.removeItem(DRAFT_KEY);
              } catch {
                /* Storage can be unavailable. */
              }
            }}
          />
        )}
      </aside>
    </div>
  );
}

/* ------------------------------------------------------------------ rail + navigation */

function Rail({
  step,
  notes,
  canReach,
  onGo,
}: {
  step: Step;
  notes: string[];
  canReach: (s: Step) => boolean;
  onGo: (s: Step) => void;
}) {
  return (
    <nav aria-label="Vault setup progress" className="lg:sticky lg:top-[100px] lg:self-start">
      <ol className="ap-rail">
        {STEPS.map((title, i) => {
          const s = i as Step;
          const state = s < step ? "done" : s === step ? "current" : "todo";
          return (
            <li key={title} data-state={state} aria-current={state === "current" ? "step" : undefined}>
              <button
                type="button"
                className="ap-rail-btn"
                disabled={state === "current" || !canReach(s)}
                onClick={() => onGo(s)}
              >
                <span className="ap-rail-dot" aria-hidden="true">
                  {state === "done" ? <Check size={15} strokeWidth={3} /> : i + 1}
                </span>
                <span className="block pt-1 max-lg:pt-0">
                  <span
                    className={cn(
                      "ap-rail-title block text-[16px] font-[550] tracking-[-0.02em]",
                      state === "current" && "text-(--ap-pink)",
                      state === "todo" && "text-(--ap-grey)",
                    )}
                  >
                    {title}
                    <span className="sr-only">
                      {state === "done" ? " (done)" : state === "current" ? " (current step)" : ""}
                    </span>
                  </span>
                  <span className="ap-rail-note mt-0.5 block text-[13.5px] text-(--ap-grey) max-lg:hidden">
                    {notes[i]}
                  </span>
                  {state === "done" && (
                    <span className="ap-rail-note mt-1.5 inline-flex items-center max-lg:hidden gap-1 text-[13px] font-[550] text-(--ap-ink) underline decoration-black/20 underline-offset-4">
                      <Pencil size={12} aria-hidden="true" /> Edit
                    </span>
                  )}
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

function StepNav({ step, onGo, busy }: { step: Step; onGo: (s: Step) => void; busy: boolean }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-4 pt-2">
      {step > 0 ? (
        <button type="button" onClick={() => onGo((step - 1) as Step)} disabled={busy} className="ap-link">
          <ArrowLeft size={16} aria-hidden="true" /> Back to {STEPS[step - 1]!.toLowerCase()}
        </button>
      ) : (
        <Link to="/app" className="ap-link">
          <ArrowLeft size={16} aria-hidden="true" /> Your vaults
        </Link>
      )}
    </div>
  );
}

function NextButton({
  step,
  error,
  onNext,
  className,
}: {
  step: Step;
  error: string | null;
  onNext: () => void;
  className?: string;
}) {
  const id = useId();
  const blocked = (step === 0 || step === DEPOSIT) && !!error;
  return (
    <div className={cn("grid gap-2", className)}>
      <button
        type="button"
        onClick={onNext}
        disabled={blocked}
        aria-describedby={blocked ? id : undefined}
        className="ap-btn ap-btn-pink w-full"
      >
        Next: {STEPS[step + 1]} <ArrowRight size={18} aria-hidden="true" />
      </button>
      {blocked && (
        <p id={id} className="text-center text-[13px] text-warn">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * Phones and tablets: the summary column sits below the page, so the mix and the Next button stay pinned
 * to the bottom of the screen instead (above the iPhone home indicator).
 */
function MobileNextBar({
  rows,
  total,
  rules,
  step,
  error,
  amountUsd,
  cash,
  onNext,
}: {
  rows: Row[];
  total: number;
  rules: Rules;
  step: Step;
  error: string | null;
  amountUsd: number;
  cash: string;
  onNext: () => void;
}) {
  const blocked = (step === 0 || step === DEPOSIT) && !!error;
  return (
    <div className="ap-nextbar fixed inset-x-0 bottom-0 z-40 border-t border-(--ap-line) bg-white/90 backdrop-blur-md lg:hidden">
      <div className={cn(APP_WRAP, "flex items-center gap-4 py-3")}>
        <div className="min-w-0 flex-1">
          <AllocBar items={rows.map((r, i) => ({ symbol: r.symbol, bps: r.bps, color: mixColor(i) }))} />
          <p
            className={cn("mt-1.5 truncate text-[13px]", blocked ? "text-warn" : "text-(--ap-grey)")}
            aria-live="polite"
          >
            {blocked
              ? error
              : step === 0
                ? `${rows.length} asset${rows.length === 1 ? "" : "s"} · ${formatPct(total, total % 100 ? 1 : 0)}`
                : step === DEPOSIT
                  ? `${formatUsd(amountUsd)} in ${cash}`
                  : `±${rules.driftThresholdBps / 100} pts · ${intervalLabel(rules.checkIntervalMin).toLowerCase()} checks`}
          </p>
        </div>
        <button
          type="button"
          onClick={onNext}
          disabled={blocked}
          className="ap-btn ap-btn-pink h-12 shrink-0 px-5"
        >
          Next: {STEPS[step + 1]} <ArrowRight size={18} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ step 1: mix */

function MixStep({
  config,
  chainId,
  rows,
  setRows,
  assetOf,
  onPreset,
}: {
  config: AppConfig | undefined;
  chainId: number;
  rows: Row[];
  setRows: React.Dispatch<React.SetStateAction<Row[]>>;
  assetOf: (s: string) => Asset | undefined;
  onPreset: (p: (typeof PRESETS)[number]) => void;
}) {
  const uid = useId();
  const chain = config?.chains.find((c) => c.id === chainId) ?? defaultChain;
  return (
    <section aria-labelledby={`${uid}-assets`} className="ap-card p-6 md:p-8">
      <h2 id={`${uid}-assets`} className="ap-h2">
        Assets and weights
      </h2>
      <Presets chainId={chainId} onPick={onPreset} />
      <AllocationEditor rows={rows} setRows={setRows} assetOf={assetOf} cash={chain.quoteAsset} />
      <Catalog
        assets={chain.assets}
        selected={rows.map((r) => r.symbol)}
        onAdd={(s) => setRows((r) => [...r, { symbol: s, bps: 0 }])}
        disabled={rows.length >= 12}
      />
    </section>
  );
}

function Presets({ chainId, onPick }: { chainId: number; onPick: (p: (typeof PRESETS)[number]) => void }) {
  const list = PRESETS.filter((p) => p.chain === chainId);
  if (!list.length) return null;
  return (
    <div className="mt-4 flex flex-wrap items-center gap-2">
      <span className="mr-1 text-[14px] text-(--ap-grey)">Start from</span>
      {list.map((p) => (
        <button type="button" key={p.name} onClick={() => onPick(p)} className="ap-chip">
          {p.name}
        </button>
      ))}
    </div>
  );
}

function AllocationEditor({
  rows,
  setRows,
  assetOf,
  cash,
}: {
  rows: Row[];
  setRows: (r: Row[]) => void;
  assetOf: (s: string) => Asset | undefined;
  /** The deposit stablecoin: always in the mix, 0% means every deposit gets invested. */
  cash: string;
}) {
  const total = rows.reduce((s, r) => s + r.bps, 0);
  const update = (i: number, bps: number) =>
    setRows(
      rows.map((r, j) => (j === i ? { ...r, bps: Math.max(0, Math.min(10_000, Math.round(bps))) } : r)),
    );
  return (
    <div className="mt-6">
      <div className="ap-panel p-4">
        <AllocBar
          items={rows.map((r, i) => ({ symbol: r.symbol, bps: r.bps, color: mixColor(i) }))}
          label={`Allocation: ${rows.map((r) => `${r.symbol} ${formatPct(r.bps, 1)}`).join(", ")}`}
        />
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
          <p
            className={cn(
              "font-mono text-[13.5px] tabular-nums",
              total === 10_000 ? "text-good" : "text-warn",
            )}
            aria-live="polite"
          >
            Total {formatPct(total, total % 100 ? 1 : 0)}{" "}
            {total === 10_000
              ? "✓"
              : `· ${total > 10_000 ? "over" : "under"} by ${formatPct(Math.abs(10_000 - total), 1)}`}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={rows.length === 0}
              onClick={() =>
                setRows(rows.map((r, i) => ({ ...r, bps: normalizeBps(rows.map(() => 1))[i]! })))
              }
              className="ap-chip h-8 px-3 text-[13px]"
            >
              Even split
            </button>
            <button
              type="button"
              disabled={rows.length === 0 || total === 0}
              onClick={() => {
                const n = normalizeBps(rows.map((r) => r.bps));
                setRows(rows.map((r, i) => ({ ...r, bps: n[i]! })));
              }}
              className="ap-chip h-8 px-3 text-[13px]"
            >
              Scale to 100%
            </button>
          </div>
        </div>
      </div>

      <ul className="mt-2" aria-label="Selected assets">
        <AnimatePresence initial={false}>
          {rows.map((r, i) => {
            const a = assetOf(r.symbol);
            return (
              <motion.li
                key={r.symbol}
                layout
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="grid grid-cols-[auto_minmax(0,1fr)_auto_auto] items-center gap-x-3 gap-y-2 border-b border-(--ap-line) py-3.5 last:border-0 sm:grid-cols-[auto_minmax(0,9rem)_minmax(0,1fr)_auto_auto] sm:gap-x-4"
              >
                <AssetIcon asset={a} symbol={r.symbol} size={38} />
                <div className="min-w-0">
                  <p className="truncate text-[15.5px] leading-[1.2] font-[550]">{a?.name ?? r.symbol}</p>
                  <p className="flex items-center gap-1.5 truncate font-mono text-[12px] text-(--ap-grey)">
                    <i
                      className="size-2 shrink-0 rounded-full"
                      style={{ background: mixColor(i) }}
                      aria-hidden="true"
                    />
                    {r.symbol}
                    {r.symbol === cash && (
                      <span className="font-sans tracking-normal">
                        · cash{r.bps === 0 ? ", all invested" : ""}
                      </span>
                    )}
                  </p>
                </div>
                <input
                  type="range"
                  className="ap-range order-last col-span-full sm:order-none sm:col-span-1"
                  min={0}
                  max={10000}
                  step={50}
                  value={r.bps}
                  onChange={(e) => update(i, Number(e.target.value))}
                  aria-label={`${r.symbol} weight`}
                  aria-valuetext={formatPct(r.bps, 1)}
                  style={{ "--p": `${r.bps / 100}%`, "--c": mixColor(i) } as React.CSSProperties}
                />
                <label className="flex h-10 items-center rounded-[12px] bg-(--ap-soft) px-2.5 focus-within:shadow-[inset_0_0_0_2px_#0b0b0b]">
                  <input
                    type="number"
                    min={0}
                    max={100}
                    step={0.5}
                    value={r.bps / 100}
                    onChange={(e) => update(i, Number(e.target.value) * 100)}
                    className="w-12 bg-transparent text-right font-mono text-[14px] tabular-nums outline-none"
                    aria-label={`${r.symbol} percent`}
                  />
                  <span className="pl-0.5 text-[14px] text-(--ap-grey)">%</span>
                </label>
                {r.symbol === cash ? (
                  <span
                    className="ap-iconbtn size-9 cursor-default text-(--ap-grey)"
                    title={`Deposits arrive in ${cash}, so it stays in every vault. Set it to 0% to invest it all.`}
                  >
                    <Lock className="size-4" aria-hidden="true" />
                    <span className="sr-only">{cash} stays in every vault</span>
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => setRows(rows.filter((_, j) => j !== i))}
                    className="ap-iconbtn size-9 hover:text-bad"
                    aria-label={`Remove ${r.symbol}`}
                  >
                    <Trash2 className="size-4" aria-hidden="true" />
                  </button>
                )}
              </motion.li>
            );
          })}
        </AnimatePresence>
        {rows.length === 0 && (
          <li className="py-6 text-center text-[15px] text-(--ap-grey)">
            No assets yet. Start from a preset or add them below.
          </li>
        )}
      </ul>
    </div>
  );
}

const KINDS = [
  { v: "all", l: "All" },
  { v: "stock", l: "Stocks" },
  { v: "stable", l: "Stables" },
  { v: "crypto", l: "Crypto" },
] as const;

function Catalog({
  assets,
  selected,
  onAdd,
  disabled,
}: {
  assets: Asset[];
  selected: string[];
  onAdd: (s: string) => void;
  disabled: boolean;
}) {
  const uid = useId();
  const [q, setQ] = useState("");
  const [kind, setKind] = useState<AssetKind | "all">("all");
  const list = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return assets
      .filter((a) => !selected.includes(a.symbol))
      .filter((a) => kind === "all" || a.kind === kind)
      .filter(
        (a) =>
          !needle ||
          [a.symbol, a.name, a.ticker ?? "", a.issuer ?? ""].some((f) => f.toLowerCase().includes(needle)),
      );
  }, [assets, selected, q, kind]);

  return (
    <div className="mt-8 border-t border-(--ap-line) pt-7">
      <h3 id={`${uid}-add`} className="text-[17px] font-[550] tracking-[-0.02em]">
        Add assets
      </h3>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <label className="ap-search min-w-0 flex-1 basis-full sm:basis-56">
          <Search className="size-4 shrink-0 text-(--ap-grey)" aria-hidden="true" />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={`Search ${assets.length} assets`}
            aria-label="Search assets"
          />
        </label>
        <fieldset className="flex flex-wrap gap-1.5">
          <legend className="sr-only">Asset type</legend>
          {KINDS.map((k) => (
            <button
              type="button"
              key={k.v}
              onClick={() => setKind(k.v)}
              aria-pressed={kind === k.v}
              className="ap-chip h-[42px] px-4"
            >
              {k.l}
            </button>
          ))}
        </fieldset>
      </div>
      {disabled && <p className="mt-3 text-[14px] text-warn">A vault holds up to 12 assets.</p>}
      <ul
        aria-labelledby={`${uid}-add`}
        className="mt-4 grid max-h-[26rem] grid-cols-[minmax(0,1fr)] gap-1 overflow-y-auto overscroll-contain pr-1 sm:grid-cols-2"
      >
        {list.map((a) => (
          <li key={a.symbol}>
            <button
              type="button"
              disabled={disabled}
              onClick={() => onAdd(a.symbol)}
              aria-label={`Add ${a.name} (${a.symbol})`}
              className="group flex w-full items-center gap-3 rounded-[16px] px-3 py-2.5 text-left transition-colors hover:bg-(--ap-soft) disabled:opacity-40"
            >
              <AssetIcon asset={a} symbol={a.symbol} size={34} />
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-2 text-[15px] font-[550] tracking-[-0.01em]">
                  <span className="truncate">{a.symbol}</span> <KindTag kind={a.kind} etf={a.etf} />
                </span>
                <span className="block truncate text-[13px] text-(--ap-grey)">
                  {a.name}
                  {a.issuer ? ` · ${a.issuer}` : ""}
                </span>
              </span>
              <span
                aria-hidden="true"
                className="grid size-8 shrink-0 place-items-center rounded-full bg-white text-(--ap-grey) shadow-[inset_0_0_0_1.5px_#e6e6df] transition-colors group-hover:text-(--ap-pink) group-hover:shadow-[inset_0_0_0_1.5px_#0b0b0b]"
              >
                <Plus className="size-4" />
              </span>
            </button>
          </li>
        ))}
        {list.length === 0 && (
          <li className="col-span-2 py-6 text-center text-[15px] text-(--ap-grey)">No matches.</li>
        )}
      </ul>
    </div>
  );
}

/* ------------------------------------------------------------------ step 2: rules */

function RuleField({
  id,
  title,
  hint,
  children,
}: {
  id: string;
  title: string;
  hint: string;
  children: ReactNode;
}) {
  return (
    <div className="grid gap-1">
      <h3 id={id} className="text-[17px] font-[550] tracking-[-0.02em]">
        {title}
      </h3>
      <p className="text-[14px] text-(--ap-grey)">{hint}</p>
      <div className="mt-3">{children}</div>
    </div>
  );
}

function RulesStep({ rules, setRules }: { rules: Rules; setRules: (r: Rules) => void }) {
  const uid = useId();
  // A restored draft can carry a band between the presets; keep it selectable.
  const bands = BANDS.some((b) => b.v === rules.driftThresholdBps)
    ? BANDS
    : [...BANDS, { v: rules.driftThresholdBps, note: "From your draft" }].sort((a, b) => a.v - b.v);
  const band = rules.driftThresholdBps / 100;
  return (
    <>
      <section aria-labelledby={`${uid}-when`} className="ap-card grid gap-8 p-6 md:p-8">
        <div>
          <p className="ap-mono">When it acts</p>
          <h2 id={`${uid}-when`} className="ap-h2 mt-2">
            Drift band and schedule
          </h2>
        </div>
        <RuleField
          id={`${uid}-band`}
          title="Rebalance band"
          hint="Act when any asset drifts this far from target"
        >
          <DriftPreview band={band} />
          <OptionSeg
            className={cn("mt-3", bands.length >= 4 && "ap-seg-wrap")}
            labelledBy={`${uid}-band`}
            value={rules.driftThresholdBps}
            onChange={(v) => setRules({ ...rules, driftThresholdBps: v })}
            options={bands.map((b) => ({ v: b.v, title: `±${b.v / 100} pts`, note: b.note }))}
          />
        </RuleField>
        <RuleField id={`${uid}-every`} title="Check every" hint="How often the agent values the vault">
          <OptionSeg
            className="ap-seg-wrap"
            labelledBy={`${uid}-every`}
            value={rules.checkIntervalMin as number}
            onChange={(v) => setRules({ ...rules, checkIntervalMin: v as Rules["checkIntervalMin"] })}
            options={INTERVALS.map((i) => ({ v: i.v as number, title: i.l, note: i.note }))}
          />
        </RuleField>
      </section>

      <section aria-labelledby={`${uid}-how`} className="ap-card grid gap-8 p-6 md:p-8">
        <div>
          <p className="ap-mono">How it trades</p>
          <h2 id={`${uid}-how`} className="ap-h2 mt-2">
            Execution limits
          </h2>
        </div>
        <div className="grid gap-8 md:grid-cols-2 md:gap-6">
          <RuleField
            id={`${uid}-slip`}
            title="Max slippage"
            hint="Enforced on-chain as the minimum you receive"
          >
            <OptionSeg
              labelledBy={`${uid}-slip`}
              value={rules.maxSlippageBps}
              onChange={(v) => setRules({ ...rules, maxSlippageBps: v })}
              options={SLIPPAGE.map((s) => ({ v: s.v, title: formatPct(s.v, 1), note: s.note }))}
            />
          </RuleField>
          <RuleField
            id={`${uid}-cost`}
            title="Cost cap per swap"
            hint="Skip a swap when gas + fees + impact exceed this"
          >
            <OptionSeg
              labelledBy={`${uid}-cost`}
              value={rules.maxCostBps}
              onChange={(v) => setRules({ ...rules, maxCostBps: v })}
              options={COSTS.map((s) => ({ v: s.v, title: formatPct(s.v, 1), note: s.note }))}
            />
          </RuleField>
        </div>
        <RuleField
          id={`${uid}-min`}
          title="Minimum swap size"
          hint="Smaller corrections wait until they're worth the gas"
        >
          <div className="flex items-center gap-4 rounded-[16px] bg-(--ap-soft) px-4 py-3">
            <input
              type="range"
              className="ap-range flex-1"
              min={5}
              max={500}
              step={5}
              value={rules.minTradeUsd}
              onChange={(e) => setRules({ ...rules, minTradeUsd: Number(e.target.value) })}
              aria-labelledby={`${uid}-min`}
              aria-valuetext={formatUsd(rules.minTradeUsd)}
              style={{ "--p": `${((rules.minTradeUsd - 5) / 495) * 100}%` } as React.CSSProperties}
            />
            <span className="w-16 text-right font-mono text-[15px] font-[550] tabular-nums">
              {formatUsd(rules.minTradeUsd)}
            </span>
          </div>
        </RuleField>
      </section>
    </>
  );
}

/** A dot drifts out of the band around its target and is brought back. */
function DriftPreview({ band }: { band: number }) {
  // Scale: ±12.5 pts across the track, target in the middle.
  const span = 12.5;
  const pos = (pts: number) => `${50 + (pts / span) * 50}%`;
  return (
    <div className="ap-panel px-5 pt-2 pb-3" aria-hidden="true">
      <div className="ap-drift">
        <span className="ap-drift-track" />
        <span className="ap-drift-band" style={{ left: pos(-band), width: `${(band / span) * 100}%` }} />
        <span className="ap-drift-target" style={{ left: "50%" }} />
        <span
          className="ap-drift-dot"
          style={{ "--t": pos(0), "--o": pos(Math.min(span - 0.5, band * 1.35)) } as React.CSSProperties}
        />
      </div>
      <div className="flex justify-between font-mono text-[11px] text-(--ap-grey)">
        <span>−{band} pts</span>
        <span>target</span>
        <span>+{band} pts</span>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ side panel, steps 1–2 */

function SoFar({
  rows,
  rules,
  total,
  amountUsd,
  cash,
  error,
  step,
  assetOf,
  onNext,
}: {
  rows: Row[];
  rules: Rules;
  total: number;
  /** Shown once the deposit step is reached. */
  amountUsd: number | null;
  cash: string;
  error: string | null;
  step: Step;
  assetOf: (s: string) => Asset | undefined;
  onNext: () => void;
}) {
  const [view, setView] = useState<"list" | "pie">("list");
  return (
    <div className="ap-card p-6">
      <div className="flex items-center justify-between gap-3">
        <h2 className="ap-h2">Your vault</h2>
        {rows.length > 0 && (
          <Seg
            label="Show weights as"
            options={[
              { v: "list", l: "List" },
              { v: "pie", l: "Pie" },
            ]}
            value={view}
            onChange={setView}
          />
        )}
      </div>
      {view === "pie" && rows.length > 0 ? (
        <AllocPie
          className="mt-5"
          items={rows.map((r, i) => ({
            symbol: r.symbol,
            bps: r.bps,
            color: mixColor(i),
            label: formatPct(r.bps, r.bps % 100 ? 1 : 0),
            icon: <AssetIcon asset={assetOf(r.symbol)} symbol={r.symbol} size={20} />,
          }))}
        />
      ) : (
        <>
          <AllocBar
            className="mt-5"
            items={rows.map((r, i) => ({ symbol: r.symbol, bps: r.bps, color: mixColor(i) }))}
          />
          <ul className="mt-4 grid gap-2.5" aria-label="Target weights">
            {rows.map((r, i) => (
              <li key={r.symbol} className="flex items-center gap-2.5 text-[14px]">
                <AssetIcon asset={assetOf(r.symbol)} symbol={r.symbol} size={24} />
                <span className="min-w-0 flex-1 truncate font-[550]">{r.symbol}</span>
                <i className="size-2 rounded-full" style={{ background: mixColor(i) }} aria-hidden="true" />
                <span className="w-14 text-right font-mono text-[13px] tabular-nums">
                  {formatPct(r.bps, r.bps % 100 ? 1 : 0)}
                </span>
              </li>
            ))}
            {rows.length === 0 && <li className="text-[14px] text-(--ap-grey)">No assets yet.</li>}
          </ul>
        </>
      )}
      <dl className="mt-5 grid gap-2 border-t border-(--ap-line) pt-4 text-[14px]">
        <div className="flex justify-between gap-4">
          <dt className="text-(--ap-grey)">Total</dt>
          <dd className={cn("font-[550] tabular-nums", total === 10_000 ? "text-good" : "text-warn")}>
            {formatPct(total, total % 100 ? 1 : 0)}
          </dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-(--ap-grey)">Band</dt>
          <dd className="font-[550]">±{rules.driftThresholdBps / 100} pts</dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-(--ap-grey)">Checks</dt>
          <dd className="font-[550]">{intervalLabel(rules.checkIntervalMin)}</dd>
        </div>
        {amountUsd !== null && (
          <div className="flex justify-between gap-4">
            <dt className="text-(--ap-grey)">First deposit</dt>
            <dd className="font-[550] tabular-nums">
              {formatUsd(amountUsd)} {cash}
            </dd>
          </div>
        )}
      </dl>
      <NextButton className="mt-6" step={step} error={error} onNext={onNext} />
    </div>
  );
}

/* ------------------------------------------------------------------ step 3: deposit */

function DepositStep({
  cash,
  cashAsset,
  amount,
  setAmount,
  balance,
  error,
  rows,
  assetOf,
}: {
  cash: string;
  cashAsset: Asset | undefined;
  amount: string;
  setAmount: (s: string) => void;
  balance: bigint | undefined;
  error: string | null;
  rows: Row[];
  assetOf: (s: string) => Asset | undefined;
}) {
  const uid = useId();
  const usd = Number(amount) || 0;
  const held = balance !== undefined && cashAsset ? formatUnits(balance, cashAsset.decimals) : null;
  return (
    <section aria-labelledby={`${uid}-amount`} className="ap-card p-6 md:p-8">
      <h2 id={`${uid}-amount`} className="ap-h2">
        How much to put in
      </h2>
      <label className="mt-5 flex items-center gap-3 rounded-[20px] bg-(--ap-soft) px-5 py-4 focus-within:shadow-[inset_0_0_0_2px_#0b0b0b]">
        <span aria-hidden="true" className="font-serif text-[44px] leading-none text-(--ap-grey)">
          $
        </span>
        <input
          inputMode="decimal"
          autoComplete="off"
          value={amount}
          onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, "").slice(0, 16))}
          aria-label={`Deposit amount in ${cash}`}
          aria-invalid={!!error}
          aria-describedby={error ? `${uid}-err` : undefined}
          placeholder="0"
          className="min-w-0 flex-1 bg-transparent font-serif text-[44px] leading-none tracking-[-0.03em] outline-none placeholder:text-black/25"
        />
        <span className="ap-pill shrink-0">
          {cashAsset && <AssetIcon asset={cashAsset} symbol={cash} size={20} />} {cash}
        </span>
      </label>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {AMOUNTS.map((v) => (
            <button
              key={v}
              type="button"
              className="ap-chip"
              aria-pressed={usd === v}
              onClick={() => setAmount(String(v))}
            >
              ${v.toLocaleString("en-US")}
            </button>
          ))}
          {held !== null && Number(held) > 0 && (
            <button type="button" className="ap-chip" onClick={() => setAmount(held)}>
              Max
            </button>
          )}
        </div>
        <p className="font-mono text-[12.5px] text-(--ap-grey)">
          Wallet:{" "}
          {held === null
            ? "…"
            : `${Number(held).toLocaleString("en-US", { maximumFractionDigits: 2 })} ${cash}`}
        </p>
      </div>
      {error && (
        <p id={`${uid}-err`} className="mt-3 text-[13.5px] text-warn" role="status">
          {error}
        </p>
      )}

      <h3 className="mt-8 text-[17px] font-[550] tracking-[-0.02em]">Where it goes</h3>
      <ul className="mt-2" aria-label="Deposit by asset">
        {rows.map((r, i) => {
          const isCash = r.symbol === cash;
          return (
            <li
              key={r.symbol}
              className="flex items-center gap-3 border-b border-(--ap-line) py-3 last:border-0"
            >
              <AssetIcon asset={assetOf(r.symbol)} symbol={r.symbol} size={32} />
              <span className="min-w-0 flex-1">
                <span className="block text-[15px] leading-[1.2] font-[550]">{r.symbol}</span>
                <span className="block text-[12.5px] text-(--ap-grey)">
                  {isCash
                    ? r.bps === 0
                      ? "Fully invested by the agent"
                      : "Kept as cash"
                    : "Bought by the agent"}
                </span>
              </span>
              <i
                className="size-2.5 shrink-0 rounded-full"
                style={{ background: mixColor(i) }}
                aria-hidden="true"
              />
              <span className="w-12 text-right font-mono text-[13px] text-(--ap-grey) tabular-nums">
                {formatPct(r.bps, r.bps % 100 ? 1 : 0)}
              </span>
              <span className="w-24 text-right text-[15px] font-[550] tabular-nums">
                {formatUsd((usd * r.bps) / 10_000)}
              </span>
            </li>
          );
        })}
      </ul>
      <p className="mt-5 rounded-[16px] bg-(--ap-soft) p-4 text-[13.5px] leading-[1.5] text-(--ap-grey)">
        Your {cash} arrives in the vault in the same transaction that creates it. On its first checks the
        agent swaps it into each asset at the best quote, a few swaps per check and always within your rules.
        Add more later from the vault page.
      </p>
    </section>
  );
}

/* ------------------------------------------------------------------ step 4: review */

function ReviewStep({
  name,
  setName,
  locked,
  rows,
  rules,
  amountUsd,
  cash,
  assetOf,
}: {
  name: string;
  setName: (s: string) => void;
  locked: boolean;
  rows: Row[];
  rules: Rules;
  amountUsd: number;
  cash: string;
  assetOf: (s: string) => Asset | undefined;
}) {
  const uid = useId();
  const facts: [string, string][] = [
    ["First deposit", `${formatUsd(amountUsd)} ${cash}`],
    ["Drift band", `±${rules.driftThresholdBps / 100} pts`],
    ["Check every", intervalLabel(rules.checkIntervalMin)],
    ["Max slippage", formatPct(rules.maxSlippageBps, 1)],
    ["Cost cap", `${formatPct(rules.maxCostBps, 1)} per swap`],
    ["Min swap", formatUsd(rules.minTradeUsd)],
    ["Agent expiry", `${AGENT_AUTH_DAYS} days`],
  ];
  return (
    <>
      <section aria-label="Vault summary" className="ap-card p-6 md:p-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <label className="grid min-w-0 grow basis-[260px] gap-1.5">
            <span className="ap-mono">Vault name</span>
            <input
              value={name}
              disabled={locked}
              onChange={(e) => setName(e.target.value.slice(0, 40))}
              maxLength={40}
              placeholder="My vault"
              className="w-full max-w-[360px] rounded-[12px] bg-(--ap-soft) px-3 py-2 text-[22px] font-[550] tracking-[-0.03em] outline-none placeholder:text-black/30 focus:shadow-[inset_0_0_0_2px_#0b0b0b]"
            />
          </label>
        </div>

        <AllocBar
          className="mt-8"
          items={rows.map((r, i) => ({ symbol: r.symbol, bps: r.bps, color: mixColor(i) }))}
        />
        <ul className="mt-5 grid gap-x-8 sm:grid-cols-2" aria-label="Assets and target weights">
          {rows.map((r, i) => {
            const a = assetOf(r.symbol);
            return (
              <li key={r.symbol} className="flex items-center gap-3 border-b border-(--ap-line) py-3">
                <AssetIcon asset={a} symbol={r.symbol} size={40} />
                <span className="min-w-0 flex-1">
                  <span className="block text-[15.5px] leading-[1.2] font-[550] break-words">
                    {a?.name ?? r.symbol}
                  </span>
                  <span className="block font-mono text-[12px] text-(--ap-grey)">{r.symbol}</span>
                </span>
                <i
                  className="size-2.5 shrink-0 rounded-full"
                  style={{ background: mixColor(i) }}
                  aria-hidden="true"
                />
                <span className="w-14 text-right text-[16px] font-[550] tabular-nums">
                  {formatPct(r.bps, r.bps % 100 ? 1 : 0)}
                </span>
              </li>
            );
          })}
        </ul>

        <h2 className="mt-9 text-[17px] font-[550] tracking-[-0.02em]">Rules</h2>
        <dl className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
          {facts.map(([k, v]) => (
            <div key={k} className="rounded-[16px] bg-(--ap-soft) px-4 py-3">
              <dt className="text-[12.5px] text-(--ap-grey)">{k}</dt>
              <dd className="mt-0.5 text-[16px] font-[550] tracking-[-0.01em]">{v}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section aria-labelledby={`${uid}-agent`} className="ap-card p-6 md:p-8">
        <h2 id={`${uid}-agent`} className="ap-h2">
          What the agent can and can't do
        </h2>
        <p className="mt-1.5 text-[14.5px] text-(--ap-grey)">
          Enforced by your vault contract, not by promises.
        </p>
        <div className="mt-6 grid gap-6 md:grid-cols-2">
          {(
            [
              ["Can", guardrails.can, true],
              ["Can't", guardrails.cant, false],
            ] as const
          ).map(([label, items, ok]) => (
            <div key={label}>
              <h3 className="ap-mono">{label}</h3>
              <ul className="mt-3 grid gap-3">
                {items.map((t) => (
                  <GuardItem key={t} ok={ok}>
                    {t}
                  </GuardItem>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}

function DeployPanel(props: {
  config: AppConfig | undefined;
  configPending: boolean;
  configError: string | null;
  chainId: number;
  name: string;
  rows: Row[];
  rules: Rules;
  error: string | null;
  /** The deposit stablecoin and the first deposit in its units (null until the amount is valid). */
  cashAsset: Asset | undefined;
  deposit: bigint | null;
  amountUsd: number;
  assetOf: (s: string) => Asset | undefined;
  allowUnpriced: boolean;
  setAllowUnpriced: (b: boolean) => void;
  onBusy: (b: boolean) => void;
  pendingVault: PendingVault | null;
  onPending: (value: PendingVault | null) => void;
  onDone: () => void;
}) {
  const { config, chainId, name, rows, rules, error, allowUnpriced, cashAsset, deposit } = props;
  const uid = useId();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const chain = config?.chains.find((c) => c.id === chainId);
  const liveReady = !!chain?.liveReady && !!chain.factory && !!config?.agentAddress;
  const unpriced = chain
    ? unpricedAssets(
        chain,
        rows.map((r) => r.symbol),
      )
    : [];
  const { address, chainId: walletChain } = useAccount();
  const wallet = useWalletStep();
  const { switchChainAsync } = useSwitchChain();
  const { writeContractAsync } = useWriteContract();
  const cid = chainId as SupportedChainId;
  const publicClient = usePublicClient({ chainId: cid });
  const [step, setStep] = useState<string | null>(null);
  // One salt per visit, so a retry after the approval targets the same predicted vault address.
  const saltRef = useRef<`0x${string}` | null>(null);
  const mounted = useRef(true);
  const connectedAccount = useRef(address);
  const submitting = useRef(false);
  const continuation = useRef<(() => void) | null>(null);
  connectedAccount.current = address;
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const create = useMutation({
    mutationFn: async () => {
      const ensureCurrent = actionGuard({ account: address, token: getToken() }, () => ({
        mounted: mounted.current,
        account: connectedAccount.current,
        token: getToken(),
      }));
      continuation.current = ensureCurrent;
      ensureCurrent();
      if (!address) throw new Error("Connect your wallet first");
      const owner = address;
      const saveProgress = (value: PendingVault | null) => {
        const saved = savePendingVault(owner, value);
        try {
          ensureCurrent();
        } catch {
          return saved;
        }
        if (saved) props.onPending(value);
        else toast.error("This browser couldn't save recovery progress. Keep this page open.");
        return saved;
      };
      let pending = readPendingVault(owner) ?? props.pendingVault;
      if (!pending) {
        if (!liveReady || !chain?.factory || !config?.agentAddress || !publicClient)
          throw new Error("Vaults aren't enabled on this network");
        if (!cashAsset || !deposit) throw new Error("Enter how much to deposit");
        const factory = chain.factory;
        const agent = config.agentAddress;
        if (error) throw new Error(error);
        const { address: _placeholder, ...input } = createVaultSchema.parse({
          name: name.trim() || "My vault",
          chainId,
          targets: rows.map((r) => ({ symbol: r.symbol, weightBps: r.bps })),
          rules,
          address: zeroAddress,
        });
        const assets = rows.map((r) => {
          const asset = props.assetOf(r.symbol);
          if (!asset) throw new Error(`${r.symbol} is not available on this network`);
          return asset.address;
        });
        if (walletChain !== chainId) {
          setStep("Switching network…");
          await switchChainAsync({ chainId: cid });
          ensureCurrent();
        }
        const expiry = BigInt(Math.floor(Date.now() / 1000) + AGENT_AUTH_DAYS * 24 * 3600);
        saltRef.current ??= keccak256(toHex(crypto.randomUUID()));
        const salt = saltRef.current;
        const predictedAddress = await publicClient.readContract({
          address: factory,
          abi: bipsFactoryAbi,
          functionName: "predictVault",
          args: [owner, salt],
        });
        ensureCurrent();
        // The vault pulls the first deposit itself while it's created: approve its address first.
        const [held, allowed] = await Promise.all([
          publicClient.readContract({
            address: cashAsset.address,
            abi: erc20Abi,
            functionName: "balanceOf",
            args: [owner],
          }),
          publicClient.readContract({
            address: cashAsset.address,
            abi: erc20Abi,
            functionName: "allowance",
            args: [owner, predictedAddress],
          }),
        ]);
        ensureCurrent();
        if (held < deposit)
          throw new Error(
            `Your wallet holds ${formatUnits(held, cashAsset.decimals)} ${cashAsset.symbol}, less than the deposit`,
          );
        if (allowed < deposit) {
          setStep(`Approve ${cashAsset.symbol} in your wallet…`);
          const approval = await writeContractAsync({
            chainId: cid,
            account: owner,
            address: cashAsset.address,
            abi: erc20Abi,
            functionName: "approve",
            args: [predictedAddress, deposit],
          });
          ensureCurrent();
          setStep(`Confirming the ${cashAsset.symbol} approval…`);
          const receipt = await publicClient.waitForTransactionReceipt({ hash: approval });
          ensureCurrent();
          if (receipt.status !== "success") throw new Error("The approval failed. Try again.");
        }
        setStep("Confirm the vault and deposit in your wallet…");
        pending = await submitPendingVault(
          { owner, factory, salt, predictedAddress, input },
          () =>
            writeContractAsync({
              chainId: cid,
              account: owner,
              address: factory,
              abi: bipsFactoryAbi,
              functionName: "createVaultWithDeposit",
              args: [
                agent,
                expiry,
                assets,
                rows.map((r) => r.bps),
                {
                  maxOracleDeviationBps: oracleDeviationBps(rules),
                  allowUnpriced: unpriced.length > 0 && allowUnpriced,
                },
                salt,
                cashAsset.address,
                deposit,
              ],
            }),
          saveProgress,
          ensureCurrent,
          publicClient,
        );
        ensureCurrent();
      }
      ensureCurrent();
      if (pending.owner.toLowerCase() !== address.toLowerCase())
        throw new Error("Reconnect the wallet that created this vault");
      if (!pending.address) {
        if (!publicClient) throw new Error("Could not connect to the vault's network");
        setStep("Confirming your vault…");
        pending = await recoverPendingVault(pending, publicClient, saveProgress, ensureCurrent);
        ensureCurrent();
      }
      ensureCurrent();
      setStep("Registering with the agent…");
      const registration = { ...pending.input, address: pending.address! };
      if (props.pendingVault) {
        // A prior POST may have committed even if its response never reached this browser.
        const tracked = await api<VaultSummary[]>("/vaults");
        ensureCurrent();
        const existing = tracked.find(
          (v) =>
            v.chainId === registration.chainId &&
            v.address?.toLowerCase() === registration.address.toLowerCase(),
        );
        if (existing) {
          const vault = await api<VaultDetail>(`/vaults/${existing.id}`);
          ensureCurrent();
          return vault;
        }
      }
      const vault = await createVault(registration);
      ensureCurrent();
      return vault;
    },
    onMutate: () => props.onBusy(true),
    onSuccess: (v) => {
      try {
        continuation.current?.();
      } catch {
        return;
      }
      if (address) savePendingVault(address, null);
      props.onPending(null);
      props.onDone();
      void qc.invalidateQueries({ queryKey: ["vaults"] });
      toast.success("Vault created and funded. The agent starts investing on its next check.");
      void navigate({ to: "/app/vault/$id", params: { id: v.id } });
    },
    onError: (e) => {
      try {
        continuation.current?.();
      } catch {
        return;
      }
      toast.error(e.message.split("\n")[0]);
    },
    onSettled: () => {
      submitting.current = false;
      if (!mounted.current) return;
      setStep(null);
      props.onBusy(false);
    },
  });

  const blocker = props.pendingVault
    ? null
    : props.configPending
      ? "Loading network availability…"
      : props.configError
        ? "Unable to check network availability. Please try again when the service reconnects."
        : (error ?? (liveReady ? null : "Vaults aren't enabled on this network yet"));
  const canDeploy = !blocker && !create.isPending;
  const failure = create.error
    ? ((create.error as { shortMessage?: string }).shortMessage ?? create.error.message).split("\n")[0]
    : null;

  return (
    <div className="ap-card p-6">
      <h2 id={`${uid}-deploy`} className="ap-h2">
        {props.pendingVault ? "Finish registration" : "Create your vault"}
      </h2>
      {props.pendingVault && (
        <p role="status" className="mt-4 rounded-[14px] bg-(--ap-soft) p-3 text-[13.5px] leading-[1.5]">
          Your creation transaction is saved. Continue to confirm it and register the same vault with the
          agent. {props.pendingVault.hash ? "Transaction: " : "Vault: "}
          <span className="font-mono break-all">
            {props.pendingVault.hash ?? props.pendingVault.predictedAddress}
          </span>
        </p>
      )}
      <div className="mt-4 grid gap-3 text-[14px] leading-[1.5] text-(--ap-grey)">
        <p>
          You'll approve {formatUsd(props.amountUsd)} {cashAsset?.symbol}, then create and fund your vault on{" "}
          {chain?.name} in one transaction, authorising the bips agent for {AGENT_AUTH_DAYS} days. The agent
          can only swap between these assets, and the vault rejects any fill more than{" "}
          {formatPct(oracleDeviationBps(rules))} below Chainlink prices. Each rebalance is one transaction.
        </p>
        {unpriced.length > 0 && (
          <label className="flex gap-3 rounded-[14px] bg-(--ap-soft) p-3 text-[13.5px]">
            <input
              type="checkbox"
              checked={allowUnpriced}
              onChange={(e) => props.setAllowUnpriced(e.target.checked)}
              disabled={create.isPending}
              className="ap-check mt-0.5"
            />
            <span>
              <span className="font-[550] text-(--ap-ink)">
                {unpriced.map((a) => a.symbol).join(", ")} {unpriced.length === 1 ? "has" : "have"} no
                Chainlink price feed.
              </span>{" "}
              Let the agent trade {unpriced.length === 1 ? "it" : "them"} anyway, bounded only by the quoted
              minimum. Leave unticked and those swaps are skipped.
            </span>
          </label>
        )}
        {address && (
          <p className="text-[12.5px]">
            From <span className="font-mono text-(--ap-ink)">{shortAddress(address)}</span>
            {walletChain !== chainId && ` · your wallet will switch to ${chain?.name}`}
          </p>
        )}
      </div>

      <dl className="mt-5 grid gap-2 border-t border-(--ap-line) pt-4 text-[14px]">
        <div className="flex justify-between gap-4">
          <dt className="text-(--ap-grey)">Transactions</dt>
          <dd className="text-right font-[550]">
            {props.pendingVault ? "Creation already submitted" : "Approve + Create + Deposit"}
          </dd>
        </div>
        {!props.pendingVault && (
          <div className="flex justify-between gap-4">
            <dt className="text-(--ap-grey)">First deposit</dt>
            <dd className="text-right font-[550] tabular-nums">
              {formatUsd(props.amountUsd)} {cashAsset?.symbol}
            </dd>
          </div>
        )}
        <div className="flex justify-between gap-4">
          <dt className="text-(--ap-grey)">Network gas</dt>
          <dd className="text-right font-[550]">Shown in your wallet</dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-(--ap-grey)">Swap costs</dt>
          <dd className="text-right font-[550]">Capped at {formatPct(rules.maxCostBps, 1)}</dd>
        </div>
      </dl>

      {blocker && (
        <p className="mt-4 text-[13.5px] text-warn" role="status">
          {blocker}
        </p>
      )}
      {wallet.signedIn ? (
        <button
          type="button"
          disabled={!canDeploy}
          onClick={() => {
            if (submitting.current) return;
            submitting.current = true;
            create.mutate();
          }}
          className="ap-btn ap-btn-pink mt-6 h-[60px] w-full text-[17.5px]"
        >
          {create.isPending ? (
            <>
              <Loader2 className="size-5 animate-spin" aria-hidden="true" /> {step ?? "Creating…"}
            </>
          ) : (
            <>
              {props.pendingVault ? "Finish registration" : "Create and fund vault"}{" "}
              <ArrowRight size={19} aria-hidden="true" />
            </>
          )}
        </button>
      ) : (
        <>
          <button
            type="button"
            onClick={wallet.run}
            disabled={wallet.pending}
            className="ap-btn ap-btn-pink mt-6 h-[60px] w-full text-[17.5px]"
          >
            <Wallet size={19} aria-hidden="true" /> {wallet.label}
          </button>
          <p className="mt-3 text-center text-[12.5px] leading-[1.45] text-(--ap-grey)">
            Your mix and rules stay as they are while you connect.
          </p>
        </>
      )}
      <p className="sr-only" aria-live="polite">
        {step ?? ""}
      </p>
      {failure && !create.isPending && (
        <p
          role="alert"
          className="mt-3 rounded-[14px] bg-[#ffe9df] p-3 text-[13.5px] leading-[1.45] text-[#c23a00]"
        >
          {failure}
        </p>
      )}
      <p className="mt-3 text-center text-[12.5px] leading-[1.45] text-(--ap-grey)">
        Pause, revoke or withdraw at any time.
      </p>
    </div>
  );
}
